import { useEffect } from 'react';
import { css } from '@emotion/react';
import { navigateToUrl } from '@frontend/auth';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';
import { FormWrap } from './form-wrap';

type Props = { redirectTo: string };

export const NativeRedirectComplete = ({ redirectTo }: Props) => {
  const { t } = useTranslation('SignIn');

  // Security: `redirectTo` is intentionally passed via TanStack router history state, NOT a URL
  // query param, so an attacker can't craft a link that auto-fires a deep link on visit. The value
  // has already been validated as a `weave://` URL pointing at an allowlisted Weave host in
  // `handlePostAuthRedirect` (and re-validated in the route's `beforeLoad` as defense in depth),
  // so it's safe to hand to `navigateToUrl` here.
  useEffect(() => {
    navigateToUrl(redirectTo, { reason: 'native deep link auto-trigger' });
    // `navigateToUrl` is a stable module-level import; including it would be noise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectTo]);

  const handleRetry = () => {
    navigateToUrl(redirectTo, { reason: 'native deep link retry' });
  };

  return (
    <FormWrap>
      <Heading
        css={css`
          margin-bottom: ${theme.spacing(2)};
        `}
      >
        {t("You're signed in")}
      </Heading>
      <Text
        css={css`
          margin-bottom: ${theme.spacing(4)};
        `}
      >
        {t("You can close this window. If Weave didn't open automatically, click the button below.")}
      </Text>
      <Button variant='tertiary' size='large' onClick={handleRetry} id='native-redirect-open-weave'>
        {t('Open Weave')}
      </Button>
    </FormWrap>
  );
};
